<script lang="ts" setup>
import {
  Component,
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useEvent } from "vue-composable";
import { dispatch } from "@/utils/bus";
import { useMenuPosition } from "@/composables/useMenuPosition";

const props = defineProps<{
  modelValue?: boolean;
  noAutoOpen?: boolean;
  target?: Record<string, any> | HTMLElement | string | Component | null;
  renderTarget?: string | HTMLElement | Component | null;
  offset?: [left: number, top: number];
  maxHeight?: number;
  id?: string;
  /**
   * Keep the menu open regardless of mouseleave / scroll on the
   * surrounding parent. Useful for menus driven by keyboard focus +
   * trigger char (e.g. "/" in an inline-entry input) where the cursor
   * may never enter the menu and the default auto-close prematurely
   * dismisses it.
   */
  persistent?: boolean;
}>();

const target = computed(() => {
  // just having something to trigger the computed
  __count.value;
  const target = props.target
    ? typeof props.target === "string"
      ? (document.querySelector(props.target) as HTMLElement)
      : (((props.target as any).$el || props.target) as HTMLElement)
    : (parent?.proxy?.$el as HTMLElement);

  if (target === proxy.$el) {
    return parent.proxy.$parent.$el;
  }
  return target;
});

const emit = defineEmits({
  "update:modelValue": (_value: boolean) => true,
  blur: (_e: FocusEvent) => true,
  focus: (_e: FocusEvent) => true,
});

provide("closeMenu", () => {
  show.value = false;
});

const menu = ref<HTMLElement | null>(null);
const content = ref<HTMLElement | null>(null);
const show = ref(props.modelValue ?? false);
const __count = ref(0);
const { proxy, parent } = getCurrentInstance()!;

const { position, updatePosition } = useMenuPosition(
  {
    offset: props.offset,
    maxHeight: props.maxHeight,
  },
  target,
  content,
  menu,
);

useEvent(target, "focusin", () => {
  if (props.noAutoOpen) return;
  show.value = true;
});

watch(show, async (s, _oldValue, onCleanup) => {
  emit("update:modelValue", s);
  await nextTick();

  // ENG-20956 bind the close-on-leave to the outer .m-menu (which includes any
  // padding) rather than the inner content, so drifting the cursor into the
  // panel's own padding ring no longer triggers a close.
  if (menu.value && !props.persistent) {
    menu.value.addEventListener("mouseleave", handleMouseLeave);
    menu.value.addEventListener("mouseenter", handleMouseEnter);
  }

  onCleanup(() => {
    if (menu.value) {
      menu.value.removeEventListener("mouseleave", handleMouseLeave);
      menu.value.removeEventListener("mouseenter", handleMouseEnter);
    }
  });
});

watch(
  menu,
  (v, _oldValue, onCleanup) => {
    if (!v) return;
    updatePosition();
    const id = window.setInterval(() => {
      updatePosition();
    }, 100);

    onCleanup(() => {
      window.clearInterval(id);
    });
  },
  { flush: "sync" },
);

watch(
  () => props.modelValue,
  (v, _oldValue, onCleanup) => {
    show.value = v;
    if (!v) return;
    updatePosition();
    const id = window.setInterval(() => {
      updatePosition();
    }, 100);

    onCleanup(() => {
      window.clearInterval(id);
    });
  },
);

const positionTop = computed(() => position.value.top);
const positionLeft = computed(() => position.value.left);
const positionWidth = computed(() => position.value.width);
const positionHeight = computed(() => position.value.height);

function handleMenuFocus() {
  const active = document.activeElement;
  // ENG-20956 a native OS overlay (Win11 predictive text / IME) briefly parks
  // focus on <body>; that is not a click-outside, so keep the menu open.
  if (!active || active === document.body) return;
  if (
    active === target.value ||
    // ENG-21073 target.value is the trigger's container; focus can land on a child <button>
    // (e.g. return-focus after a date field closes) — recognise it as still on the trigger.
    (target.value as HTMLElement | null)?.contains?.(active) ||
    active === proxy?.$el ||
    (proxy?.$el as HTMLElement).contains(active) ||
    // ENG-21073 the menu content is teleported, so proxy.$el (the anchor) doesn't contain
    // it — check the rendered .m-menu element so focus on real menu content (e.g. a date
    // field's trigger) is recognised as "within" the menu.
    menu.value?.contains(active) ||
    // ENG-20855 the date-picker calendar teleports to <body> (outside the menu) and
    // auto-focuses a day on open; treat focus inside it as still "within" the menu so
    // opening a date filter doesn't dismiss the menu.
    active.closest?.(".m-calendar-grid")
  ) {
    return;
  }
  dispatch("close-menus", { target: props.id });
  return (show.value = false);
}

// ENG-20956 grace delay before closing on leave; cancelled by handleMouseEnter
// when the cursor returns, so a brief stray outside the panel no longer dismisses it.
const CLOSE_DELAY_MS = 650;
let closeTimer: ReturnType<typeof setTimeout> | undefined;

function handleMouseLeave(e?: Event) {
  // ENG-20855 the date-picker calendar teleports to <body> (outside the menu); moving the
  // cursor into it must not start the close timer — it's a logical child of the menu.
  const to = (e instanceof MouseEvent ? (e.relatedTarget as Element | null) : null) ?? null;
  if (to && to.closest?.(".m-calendar-grid")) return;
  // ENG-21066 OS/browser suggestion overlays (Win11 predictive text, autofill
  // dropdowns) fire a mouseleave even though the pointer never left the panel.
  // A phantom leave always has clientX/Y still inside the menu bounds; a real
  // drift-away does not.
  if (e instanceof MouseEvent && menu.value) {
    const rect = menu.value.getBoundingClientRect();
    const stillInside =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;
    if (stillInside) return;
  }
  const active = document.activeElement;
  const leftToNativeOverlay = e instanceof MouseEvent && e.relatedTarget === null;
  const focusInside = !!active && (proxy?.$el as HTMLElement)?.contains(active);
  if (leftToNativeOverlay || focusInside) return;
  clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    show.value = false;
  }, CLOSE_DELAY_MS);
}

function handleMouseEnter() {
  clearTimeout(closeTimer);
}

onMounted(() => {
  // trigger target
  __count.value++;
  window.addEventListener("focus", handleMenuFocus, true);
  if (!props.persistent) {
    menu.value?.parentElement?.addEventListener("mouseleave", handleMouseLeave);
    menu.value?.parentElement?.addEventListener("scroll", handleMouseLeave);
  }
});

onUnmounted(() => {
  window.removeEventListener("focus", handleMenuFocus, true);
  content.value?.parentElement?.removeEventListener("mouseleave", handleMouseLeave);
  content.value?.parentElement?.removeEventListener("scroll", handleMouseLeave);
});

defineExpose({
  hide() {
    show.value = false;
  },
  show() {
    show.value = true;
  },
});
</script>
<template>
  <teleport v-if="renderTarget" :to="renderTarget">
    <div
      v-if="show"
      :id="id"
      ref="menu"
      class="m-menu"
      tabindex="-1"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
    >
      <div ref="content" class="m-menu--content">
        <slot />
      </div>
    </div>
  </teleport>

  <div
    v-else-if="show"
    :id="id"
    ref="menu"
    class="m-menu"
    tabindex="-1"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    <div ref="content" class="m-menu--content">
      <slot />
    </div>
  </div>
</template>

<style lang="scss">
.m-menu {
  animation: fadeIn 0.1s ease both;
  animation-delay: 50ms;

  position: fixed;
  z-index: 9002;

  @media (min-width: 505px) {
    top: v-bind(positionTop);
    left: v-bind(positionLeft);
    width: v-bind(positionWidth);
    max-height: v-bind(positionHeight);
  }
  will-change: auto;
  box-shadow:
    0px 4px 6px rgba(0, 0, 0, 0.06),
    0px 2px 4px rgba(0, 0, 0, 0.06);

  background-color: white;
  border-radius: 4px;
  overflow: hidden;

  display: flex;

  .m-menu--content {
    flex: 1 1 auto;
    overflow: auto;
  }
}
</style>
